import classNames from "classnames";
import pluralize from "pluralize";
import { Tooltip } from "antd";
import ChevronIcon from "assets/icons/ChevronIcon";
import TextWithTooltip from "views/components/TextWithTooltip";
import { formatNumber } from "helpers/utility/formatters";
import styles from "./style.module.scss";

export interface CounterItem {
  id: string;
  label: string;
  count: number;
  /** net change made by the user, hidden when 0 / undefined */
  delta?: number;
  /** dashed style, used for the virtual Unlabeled class */
  dashed?: boolean;
  /** shown when hovering the item */
  tooltip?: string;
}

interface CountersProps {
  items: CounterItem[];
  selectedId?: string | null;
  onClick?: (id: string) => void;
}

const formatDelta = (delta: number) => (delta > 0 ? `+${delta}` : `${delta}`);

/** List of labels with their image count and the changes made to each one */
export default function Counters({
  items,
  selectedId,
  onClick,
}: CountersProps) {
  return (
    <div className={styles.wrapper} data-testid="counters">
      {items.map((item) => (
        <Tooltip key={item.id} title={item.tooltip} mouseEnterDelay={0.4}>
          <div
            role={onClick ? "button" : undefined}
            data-testid="counter-item"
            className={classNames(styles.item, {
              [styles.selected]: item.id === selectedId,
              [styles.dashed]: item.dashed,
              [styles.clickable]: !!onClick,
            })}
            onClick={() => onClick?.(item.id)}
          >
            <TextWithTooltip text={item.label} className={styles.label} />
            <div className={styles.counts}>
              <span>{formatNumber(item.count)}</span>
              {!!item.delta && (
                <Tooltip
                  title={`${formatDelta(item.delta)} ${pluralize(
                    "image",
                    Math.abs(item.delta),
                  )} changed by you`}
                  mouseEnterDelay={0.4}
                >
                  <span
                    data-testid="counter-delta"
                    className={classNames(styles.delta, {
                      [styles.positive]: item.delta > 0,
                      [styles.negative]: item.delta < 0,
                    })}
                  >
                    {formatDelta(item.delta)}
                  </span>
                </Tooltip>
              )}
              {onClick && <ChevronIcon direction="right" />}
            </div>
          </div>
        </Tooltip>
      ))}
    </div>
  );
}
